import React, { useContext, useMemo, useState } from "react";
import { Checkbox, Icon, message, Modal, Button, Tooltip } from "antd";
import {
    SortableContainer,
    SortableElement,
    SortableHandle
} from "react-sortable-hoc";
import {
    errorMessage,
    getQuestionTypeName,
    QuestionType
} from "../../../../../service/service";
import { TopicType } from "../../../../../service/homework";
import usePromiseCallback from "../../../../../utils/usePromiseCallback";
import PaperContext from "../../context";
import { deleteExamTopic, topicSort } from "../../../../../service/exam";

import style from "./WidgetQuestBlock.module.scss";
import { OrderTopicType } from "../..";
import arrayMove from "array-move";
import _ from "underscore";
import { useToggle } from "react-use";
import { CheckboxChangeEvent } from "antd/es/checkbox";

interface WidgetQuestBlockProps {
    end?: React.ReactNode;
    active?: boolean;
    type: QuestionType;
    list: OrderTopicType[];
    onToggle: (active: boolean) => void;
}

export default function WidgetQuestBlock({
    end,
    type,
    onToggle,
    list
}: WidgetQuestBlockProps) {
    const { setTopics, topics, addTopic, lockEdit, loadPaperData } =
        useContext(PaperContext);
    const [selected, setSelected] = useState<TopicType[]>([]);
    const [visible, setVisible] = useState(false);

    // 弹出框取消后，清空一切操作
    const handleCancel = () => {
        setVisible(false);
    };

    const delHtmlTag = (str: string) => {
        return str.replace(/<[^>]+>/g, "");
    };

    const onChange = (topic: TopicType) => {
        if (isChecked(topic)) {
            setSelected(selected => {
                return selected.filter(item => item !== topic);
            });
        } else {
            setSelected(selected => {
                return [...selected, topic];
            });
        }
    };

    const isChecked = (topic: TopicType) => {
        return selected.includes(topic);
    };

    const onRemove = usePromiseCallback(async () => {
        if (selected.length === 0)
            return Promise.resolve(message.info("请选择要删除的题目!"));

        return deleteExamTopic(selected)
            .then(() =>
                setTopics(topics.filter(item => !selected.includes(item)))
            )
            .then(() => setSelected([]))
            .then(() => loadPaperData())
            .then(() => list.length === selected.length && onToggle(false))
            .then(() => !!message.info("删除成功!"))
            .catch(e => message.error(errorMessage(e)));
    });

    const onAddClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        if (lockEdit) {
            message.warn("学生已经开始答题, 无法编辑！");
            return;
        }
        onToggle(true);
        addTopic(type);
    };

    const handleSortable = ({
        oldIndex,
        newIndex
    }: {
        oldIndex: any;
        newIndex: any;
    }) => {
        const topicList = [...topics];
        const array = moveArray(
            _.sortBy(
                topicList.filter(topic => topic.topicType === type),
                topic => topic.order
            ),
            oldIndex,
            newIndex
        );
        array.forEach(item => {
            const index = topicList.findIndex(
                topic => topic.tempTopicId === item.tempTopicId
            );
            if (index >= 0) topicList[index] = item;
        });
        topicSort(array);
        setTopics(topicList);
    };

    const moveArray = (list: OrderTopicType[], from: number, to: number) => {
        return arrayMove(list, from, to).map((item, index) => {
            item.order = index;
            return item;
        });
    };

    const filterTopicByType = (type: QuestionType) => {
        return topics.filter((item: any) => item.topicType === type);
    };

    return (
        <WidgetQuestBlockPure
            list={filterTopicByType(type)}
            lockEdit={lockEdit}
            end={end}
            onAddClick={onAddClick}
            type={type}
            visible={visible}
            setVisible={setVisible}
            handleCancel={handleCancel}
            onRemove={onRemove}
            handleSortable={handleSortable}
            isChecked={isChecked}
            delHtmlTag={delHtmlTag}
            onChange={onChange}
        />
    );
}

export interface WidgetQuestBlockPureProps {
    list: OrderTopicType[];
    lockEdit: boolean;
    end: React.ReactNode;
    onAddClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    type: QuestionType;
    visible: boolean;
    setVisible: (val: boolean) => void;
    handleCancel: () => void;
    onRemove: () => void;
    handleSortable: ({
        oldIndex,
        newIndex
    }: {
        oldIndex: any;
        newIndex: any;
    }) => void;
    isChecked: (topic: TopicType) => boolean;
    delHtmlTag: (str: string) => void;
    onChange: (topic: TopicType) => void;
}

export function WidgetQuestBlockPure({
    list,
    end,
    handleCancel,
    handleSortable,
    lockEdit,
    onAddClick,
    onRemove,
    setVisible,
    type,
    visible,
    isChecked,
    delHtmlTag,
    onChange
}: WidgetQuestBlockPureProps) {
    const DragHandle = SortableHandle(() => <Icon type="drag" />);

    const SortableItem = SortableElement(
        ({
            indexTag,
            value,
            className
        }: {
            indexTag: number;
            value: TopicType;
            className?: string;
        }) => {
            return (
                <div className={className}>
                    <DragHandle />
                    &nbsp;{" "}
                    <Checkbox
                        checked={isChecked(value)}
                        onChange={e => onChange(value)}>
                        {indexTag}.{delHtmlTag(value.trunk)}
                    </Checkbox>
                </div>
            );
        }
    );

    const SortableList = SortableContainer(
        ({ list }: { list: TopicType[] }) => {
            return (
                <div>
                    {list.map((value, index) => (
                        <SortableItem
                            className={style.sortableitem}
                            key={`item-${value.tempTopicId}`}
                            index={index}
                            indexTag={index + 1}
                            value={value}
                        />
                    ))}
                </div>
            );
        }
    );

    const [all, toggleAll] = useToggle(false);

    function handToggle(e: CheckboxChangeEvent) {
        toggleAll();
        list.forEach(value => {
            if (e.target.checked) {
                if (!isChecked(value)) {
                    onChange(value);
                }
            } else {
                if (isChecked(value)) {
                    onChange(value);
                }
            }
        });
    }

    const indeterminate = useMemo(() => {
        const ul = list.filter(i => isChecked(i));
        if (ul.length === 0) {
            toggleAll(false);
            return false;
        }
        if (ul.length === list.length) {
            toggleAll(true);
            return false;
        }
        toggleAll(false);
        return true;
    }, [list]);

    return (
        <div className={style.root}>
            <h1 className={style.title}>
                <span>
                    {getQuestionTypeName(type)} <span>{list.length}题</span>
                </span>
                <div className={style.btnRow}>
                    <Tooltip title="点击图标调整该组下的题目顺序或删除某道题">
                        <Icon
                            type="edit"
                            onClick={() =>
                                list.length !== 0 && setVisible(true)
                            }
                        />
                    </Tooltip>
                    <Tooltip title="点击图标添加该题型的题目">
                        <Icon type="plus" onClick={onAddClick} />
                    </Tooltip>
                    {end}
                </div>
            </h1>

            <Modal
                title={getQuestionTypeName(type)}
                visible={visible}
                onOk={() => setVisible(false)}
                onCancel={handleCancel}
                footer={[
                    <Checkbox
                        checked={all}
                        onChange={handToggle}
                        key="a"
                        indeterminate={indeterminate}>
                        全选
                    </Checkbox>,
                    <Button key="deleteBtn" type="danger" onClick={onRemove}>
                        删除
                    </Button>
                ]}>
                <div className={style.sortableWrap}>
                    <SortableList
                        lockAxis="y"
                        useDragHandle
                        helperClass={style.sortable}
                        onSortEnd={handleSortable}
                        list={_.chain(list)
                            .sortBy(item => item.order)
                            .value()}
                    />
                </div>
            </Modal>
        </div>
    );
}
